<template>
  <match-media v-slot="{ mobile }">
    <v-dialog
      :title="mobile ? 'Запрос на изменения' : ''"
      :visible.sync="localVisible"
      size="extra-large"
      class="customer-edit-pending-dialog"
    >
      <v-form class="customer-edit-pending-dialog__form" @submit.prevent="onSubmit">
        <template v-if="mobile" #header>
          <v-tabs v-model="activeTab" class="customer-edit-pending-dialog__form-tabs" primary>
            <v-tab name="left">
              {{ $options.TABS_LABELS_MAP[editMode ? 'SEND' : 'BEFORE_SENDING'] }}
            </v-tab>
            <v-tab name="right">
              {{ $options.TABS_LABELS_MAP[editMode ? 'EDIT' : 'SEND'] }}
            </v-tab>
          </v-tabs>
        </template>
        <div class="customer-edit-pending-dialog__form-body">
          <customer-edit-pending-dialog-form-content-wrapper
            v-for="({ props, params }, index) in formColumns"
            v-show="params.show"
            :key="index"
            v-bind="props"
            class="customer-edit-pending-dialog__form-content-wrapper"
            :class="{ 'customer-edit-pending-dialog__form-content-wrapper--mobile': mobile }"
            :agent-options="agentOptions"
            :status-options="statusOptions"
            :call-source-options="callSourceOptions"
            @change="setLocalCustomerData"
          />
        </div>

        <template #footer>
          <v-button class="customer-edit-pending-dialog__form-button" @click.prevent="editMode = !editMode">{{
            editMode ? 'Отменить' : 'Редактировать запрос'
          }}</v-button>
          <v-button
            v-if="editMode"
            :disabled="isFieldsNotChanged"
            class="customer-edit-pending-dialog__form-button"
            primary
            type="submit"
            >{{ isRoleAdmin ? 'Применить изменения' : 'Отправить обновленный запрос' }}
          </v-button>
        </template>
      </v-form>
    </v-dialog>
  </match-media>
</template>

<script>
import VDialog from '@/components/common/VDialog.vue'
import { agentsService, customersService } from '@/services/http'
import { formatAgentObject } from '@/utils/formatters'
import { cloneDeep, getFirstErrorForFields, isFieldsNotChanged } from '@/utils/common'
import { loadingService } from '@/services/loading'
import VButton from '@/components/common/VButton.vue'
import VForm from '@/components/form/VForm.vue'
import { mapGetters } from 'vuex'
import { MODULE_SESSION } from '@/store/modules/session/session.types'
import VTabs from '@/components/common/VTabs.vue'
import VTab from '@/components/common/VTab.vue'
import { MatchMedia } from 'vue-component-media-queries'
import CustomerEditPendingDialogFormContentWrapper from '@/components/Customer/EditPendingDialogFormContentWrapper.vue'

const TABS_LABELS_MAP = {
  BEFORE_SENDING: 'До отправки',
  SEND: 'Отправлено',
  EDIT: 'Редактирование'
}

const EMPTY_CUSTOMER = {
  name: '',
  phone: '',
  email: '',
  agent: null,
  status: null,
  callSource: null
}

export default {
  TABS_LABELS_MAP,
  name: 'CustomerEditPendingDialog',
  components: {
    CustomerEditPendingDialogFormContentWrapper,
    VTab,
    VTabs,
    VForm,
    VButton,
    VDialog,
    MatchMedia
  },
  props: {
    visible: { type: Boolean, required: true },
    customerData: {
      type: Object,
      default: () => {
        return {}
      }
    },
    draftData: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  data() {
    return {
      activeTab: 'right',
      localCustomerData: {},
      loading: false,
      errors: {},
      agentOptions: [],
      statusOptions: [],
      callSourceOptions: [],
      editMode: false
    }
  },
  inject: ['mediaQueries'],
  computed: {
    isFieldsNotChanged() {
      return isFieldsNotChanged(this.customerData, this.localCustomerData, { isAdmin: this.isRoleAdmin })
    },
    formColumns() {
      return [
        {
          props: {
            title: this.editMode ? TABS_LABELS_MAP.SEND : TABS_LABELS_MAP.BEFORE_SENDING,
            customerData: this.editMode ? this.formattedDraftData : this.formattedCustomerData,
            disableFields: true,
            editMode: false,
            backendErrors: null
          },
          params: {
            show: !this.mediaQueries.mobile || this.isFormType('left')
          }
        },
        {
          props: {
            title: this.editMode ? TABS_LABELS_MAP.EDIT : TABS_LABELS_MAP.SEND,
            customerData: this.editMode ? this.localCustomerData : this.formattedDraftData,
            disableFields: !this.editMode,
            editMode: this.editMode,
            backendErrors: this.errors
          },
          params: {
            show: !this.mediaQueries.mobile || this.isFormType('right')
          }
        }
      ]
    },
    ...mapGetters({
      isRoleAdmin: `${MODULE_SESSION}/isRoleAdmin`
    }),
    formattedCustomerData() {
      const local = { ...EMPTY_CUSTOMER, ...cloneDeep(this.customerData) }
      return local
    },
    formattedDraftData() {
      const local = { ...EMPTY_CUSTOMER, ...cloneDeep(this.draftData) }
      return local
    },
    localVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.editMode = false
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    editMode: {
      handler() {
        this.localCustomerData = this.formattedDraftData
        this.errors = {}
      }
    },
    loading(val) {
      loadingService.setGlobalLoading(val)
    }
  },
  created() {
    if (this.isRoleAdmin) {
      this.selectAgents()
    }
    this.selectStatuses()
    this.selectCallSources()
  },
  methods: {
    isFormType(type) {
      return this.activeTab === type
    },
    setLocalCustomerData(newData) {
      this.localCustomerData = newData
    },
    selectAgents() {
      return agentsService.select().then(agentList => {
        this.agentOptions = agentList.map(agent => formatAgentObject(agent))
      })
    },
    selectStatuses() {
      return customersService.getCustomerStatusesMap().then(statusesList => {
        this.statusOptions = statusesList
      })
    },
    selectCallSources() {
      return customersService.getCallSourcesMap().then(callSourcesList => {
        this.callSourceOptions = callSourcesList
      })
    },
    onSubmit() {
      const isEditProfile = true
      this.loading = true
      customersService
        .update(this.customerData.id, this.localCustomerData, isEditProfile)
        .then(newData => {
          this.$emit('submitted', newData)
          this.localVisible = false
        })
        .catch(error => {
          const flatErrors = { ...error.details, ...error.details.customerProfile }
          this.errors = getFirstErrorForFields(flatErrors)
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>
